import React from 'react';
import { Box, Typography, Paper, IconButton } from '@mui/material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { documentCardSx } from '../../theme/terminalStyles';
import PickingStatusDot from './PickingStatusDot';

/** порожньо | процес | готово | поза документом */
export const getRowPickingStatus = (row) => {
  if (row.isOrphan || (!row.saleProduct && !row.isExtra)) return 'orphan';
  const fact = Number(row.pickLine?.quantity ?? 0);
  if (row.isExtra || row.saleProduct?.quantity == null) {
    if (fact <= 0 && fact > -1e9) return 'empty';
    return 'full';
  }
  const doc = Number(row.saleProduct.quantity);
  if (fact <= 0 && fact > -1e9) return 'empty';
  if (fact >= doc - 0.0001) return 'full';
  return 'partial';
};

const LIST_STATUS_DOT_SIZE = 24;

const formatArticle = (product) => {
  const a = product?.article;
  if (a == null || String(a).trim() === '') return '—';
  return String(a).trim();
};

const PickingLineCard = ({ row, product, onOpen, onDelete, isSpotlighted, deleteEnabled, hideStock }) => {
  const article = formatArticle(product);
  const name = product ? `${product.name || ''} ${product.modName || ''}`.trim() : '—';
  const doc = row.isExtra || !row.saleProduct || row.saleProduct.quantity == null
    ? null
    : Number(row.saleProduct.quantity);
  const stock = Number(row.pickLine?.quantityStockCurrent ?? 0);
  const fact = Number(row.pickLine?.quantity ?? 0);
  const status = getRowPickingStatus(row);
  const isOrphan = Boolean(row.isOrphan);
  const canDelete = (isOrphan || Boolean(row.isExtra)) && deleteEnabled;

  const rowKeyStable = row.saleProduct?.id ?? `orp-${row.pickLine?.id}`;

  return (
    <Paper
      data-row-key={rowKeyStable}
      sx={{
        ...documentCardSx,
        position: 'relative',
        pr: { xs: canDelete ? 9 : 5, sm: canDelete ? 9 : 5 },
        ...(isOrphan
          ? {
              bgcolor: '#fce4ec',
              border: '2px solid #f48fb1',
              boxShadow: '0 2px 10px rgba(244, 143, 177, 0.25)',
            }
          : {}),
        ...(isSpotlighted
          ? {
              '@keyframes pickingRowGlow': {
                '0%, 100%': { boxShadow: '0 2px 10px rgba(0, 0, 0, 0.07)' },
                '50%': {
                  boxShadow: '0 0 0 3px rgba(255, 179, 71, 0.9), 0 4px 16px rgba(0, 0, 0, 0.15)',
                },
              },
              animation: 'pickingRowGlow 0.7s ease-in-out 4',
              borderColor: '#FFB347',
            }
          : {}),
      }}
      elevation={0}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <IconButton
          aria-label="відкрити"
          size="small"
          onClick={onOpen}
          sx={{ color: 'text.primary' }}
        >
          <OpenInNewOutlinedIcon />
        </IconButton>
        {canDelete ? (
          <IconButton
            aria-label="видалити"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(row);
            }}
            sx={{ color: '#c62828' }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'nowrap' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            bgcolor: isOrphan ? 'rgba(244, 143, 177, 0.35)' : 'rgba(0,0,0,0.06)',
            borderRadius: 1,
            px: 1,
            py: 0.25,
            flexShrink: 0,
            minWidth: 28,
            textAlign: 'center',
          }}
        >
          {row.displayOrder ?? '—'}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0, pr: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25, wordBreak: 'break-word' }}>
            Артикул: <strong>{article}</strong>
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-word', pr: 0.5 }}>
            {name}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mt: 0.75,
              minWidth: 0,
            }}
          >
            <Typography variant="body2" sx={{ minWidth: 0 }}>
              {hideStock ? null : (
                <>
                  Залишок: <strong>{stock}</strong>
                  {' · '}
                </>
              )}
              {doc != null ? (
                <>
                  Док: <strong>{doc}</strong>
                  {' · '}
                </>
              ) : null}
              Зібрано: <strong>{fact}</strong>
            </Typography>
            <PickingStatusDot status={status} size={LIST_STATUS_DOT_SIZE} />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default PickingLineCard;
